import { useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import CustomDropdown from "../../components/forms/CustomDropdown";
import ItemBrowser from "../../components/forms/ItemBrowser";
import { plansApi } from "../../services/plans/plansApi";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { sessionTypesApi } from "../../services/nomenclatures/sessionTypesApi";
import { examTypesApi } from "../../services/nomenclatures/examTypesApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import { ProgressSpinner } from "primereact/progressspinner";
import Error from "../../components/common/Error";
import * as Yup from "yup";

interface FormValues {
  planId: number | null;
  sessionTypeId: number | null;
  examTypeId: number | null;
  academicYearId: number | null;
}

const GenerateProtocolsForm = () => {
  const toast = useRef<Toast>(null);
  const [generateProtocols, { isLoading: isGenerating, error: generateError }] =
    protocolsApi.useGenerateProtocolsFromPlanMutation();
  const { data: sessionTypes } = sessionTypesApi.useGetSessionTypesQuery();
  const { data: examTypes } = examTypesApi.useGetExamTypesQuery();
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const [triggerGetPlans, { isLoading: isPlansLoading }] = plansApi.useLazyGetPlansQuery();

  const validationSchema = Yup.object().shape({
    planId: Yup.number().required("Полето е задължително"),
    sessionTypeId: Yup.number().required("Полето е задължително"),
    examTypeId: Yup.number().required("Полето е задължително"),
    academicYearId: Yup.number().required("Полето е задължително"),
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      planId: null,
      sessionTypeId: null,
      examTypeId: null,
      academicYearId: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!values.planId || !values.sessionTypeId || !values.examTypeId || !values.academicYearId) return;

      try {
        const response = await generateProtocols({
          planId: values.planId,
          sessionTypeId: values.sessionTypeId,
          examTypeId: values.examTypeId,
          academicYearId: values.academicYearId,
        }).unwrap();

        if (response.data) {
          toast.current?.show({
            severity: "success",
            summary: "Успех",
            detail: response.data.message || "Протоколите са генерирани успешно",
          });
          formik.resetForm();
        }
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: "Възникна грешка при генерирането на протоколите",
        });
      }
    },
  });

  return (
    <div>
      <Card>
        <form onSubmit={formik.handleSubmit} className="flex flex-column gap-2">
          <FormikProvider value={formik}>
            <ItemBrowser
              id="planId"
              label="Учебен план"
              optionsPromise={() => triggerGetPlans({})}
              loading={isPlansLoading}
              idField="planId"
              labelField="code"
              columns={[
                {
                  field: "planId",
                  header: "ID",
                  sortable: true,
                  filter: true,
                  body: (rowData: any) => (
                    <span className={rowData.isActive === false ? "text-color-secondary opacity-60" : ""}>
                      {rowData.planId}
                    </span>
                  ),
                },
                { field: "code", header: "Код", sortable: true, filter: true },
                { field: "specialityName", header: "Специалност", sortable: true, filter: true },
                { field: "degreeName", header: "ОКС", sortable: true, filter: true },
                { field: "specialityModeName", header: "Форма на обучение", sortable: true, filter: true },
                { field: "startAcademicYearYearNumber", header: "Начална година", sortable: true, filter: true },
              ]}
            />

            <CustomDropdown
              id="sessionTypeId"
              label="Сесия"
              options={sessionTypes?.data || []}
              optionLabel="name"
              optionValue="sessionTypeId"
              placeholder="Изберете сесия"
            />

            <CustomDropdown
              id="examTypeId"
              label="Тип изпит"
              options={examTypes?.data || []}
              optionLabel="name"
              optionValue="examTypeId"
              placeholder="Изберете тип изпит"
            />

            <CustomDropdown
              id="academicYearId"
              label="Академична година"
              options={academicYears?.data || []}
              optionLabel="name"
              optionValue="academicYearId"
              placeholder="Изберете академична година"
            />

            <div className="flex justify-content-end">
              <Button
                type="submit"
                label="Генериране"
                icon="pi pi-plus"
                severity="success"
                disabled={isGenerating || !formik.isValid}
              />
            </div>
          </FormikProvider>
        </form>
      </Card>

      {isGenerating && <ProgressSpinner />}
      {generateError && <Error error={generateError} />}
      <Toast ref={toast} />
    </div>
  );
};

export default GenerateProtocolsForm;
